import { localPoint } from '@vx/event'
import PropTypes from 'prop-types'
import { Fragment, useRef, useState, useEffect, useCallback } from 'react'
import useWindowSize from 'react-use/lib/useWindowSize'

import Text from 'components/common/Text'
import theme from 'theme'

const tooltipCSSTransition =
  'all 250ms cubic-bezier(0, 0, 0, 1), opacity 0ms ease'

/**
 * Utility hook for target tooltip management
 *
 * @param {object} options
 * @param {number} [options.clearTimeoutDelay = 0] - Delay between mouseleave and closing the tooltip
 * @param {number} [options.offsetTop = 16] - Top offset of tooltip from target
 * @param {getPosition} options.getPosition - Function to calculate tooltips position manually
 *
 * @returns {{tooltipConfig: *, clearTargetTooltip: clearTargetTooltip, setTargetTooltip: setTargetTooltip, localPoint: { x, y}}}
 */
export function useChartTooltip({
  clearTimeoutDelay = 0,
  offsetTop = 0,
  offsetLeft = theme.space[2],
  windowBuffer = { x: theme.space[1], y: theme.space[7] },
  getPosition,
  customStyles
} = {}) {
  const { width: windowWidth, height: windowHeight } = useWindowSize()
  const clearTooltipTimeoutRef = useRef()
  const hasShownRef = useRef()
  const tooltipRef = useRef()
  const boundingElRef = useRef()
  const [tooltipConfig, setTooltipConfig] = useState({
    display: false,
    tooltipRef,
    boundingElRef,
    offsetLeft,
    offsetTop,
    svgX: 0,
    svgY: 0,
    style: {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    },
    customStyles: {}
  })

  useEffect(() => {
    const handleScroll = () => {
      if (tooltipConfig.display && clearTargetTooltip) clearTargetTooltip()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If not using custom positioning, ensure that tooltip is in the bounds of the
  // window or bounding element. Flip tooltip to left/bottom if necessary
  useEffect(() => {
    if (tooltipConfig.display && !hasShownRef.current) {
      if (tooltipRef.current && typeof getPosition !== 'function') {
        let left = tooltipConfig.style.left
        let top = tooltipConfig.style.top

        const parentRect = boundingElRef.current
          ? boundingElRef.current.getBoundingClientRect()
          : {
              top: windowBuffer.y,
              bottom: windowHeight - windowBuffer.y,
              left: windowBuffer.x,
              right: windowWidth - windowBuffer.x
            }

        const tooltipRect = tooltipRef.current.getBoundingClientRect()
        const targetRect = tooltipConfig.targetEl.getBoundingClientRect()

        const useAbsolute =
          tooltipConfig.style.position === 'absolute' && boundingElRef.current

        const isOutsideBoundingElRight =
          left + tooltipRect.width + offsetLeft > parentRect.width

        const isOutsideWindowRight =
          left + tooltipRect.width > windowWidth - windowBuffer.x

        if (isOutsideBoundingElRight || isOutsideWindowRight) {
          left = left - targetRect.width - tooltipRect.width - offsetLeft * 2
        }

        // Too high
        if (useAbsolute) {
          if (top - offsetTop < 0) {
            top = 0
          }
        } else if (top < windowBuffer.y) {
          top = windowBuffer.y
        }

        const bottomThreshold = useAbsolute
          ? parentRect.height
          : windowHeight - windowBuffer.y

        // Too low
        if (top + tooltipRect.height > bottomThreshold) {
          top -= top + tooltipRect.height - bottomThreshold
        }

        left = Math.round(left)
        top = Math.round(top)
        setTooltipConfig((s) => ({
          ...s,
          style: {
            ...s.style,
            top,
            left,
            opacity: 1
          }
        }))
      }
    }

    // keep track of previous `display` value
    hasShownRef.current = tooltipConfig.display
  }, [
    tooltipConfig.display,
    getPosition,
    windowBuffer,
    tooltipConfig.style.left,
    tooltipConfig.style.top,
    tooltipConfig.style.position,
    tooltipConfig.targetEl,
    windowHeight,
    windowWidth,
    offsetLeft,
    offsetTop
  ])

  // utility for setting display to false while keeping rest of state
  const hideTooltip = useCallback(
    () => setTooltipConfig((s) => ({ ...s, display: false })),
    []
  )

  const setTargetTooltip = useCallback(
    (event, data) => {
      clearTimeout(clearTooltipTimeoutRef.current)
      const targetEl = event?.target
      if (!targetEl) {
        return hideTooltip()
      }

      // x,y coordiates of the event on the svg document instead of window
      const { x, y } = localPoint(event) ?? { x: 0, y: 0 }

      let style = {}

      const targetRect = targetEl.getBoundingClientRect()

      if (typeof getPosition === 'function') {
        style = Object.assign(
          style,
          getPosition({
            targetRect,
            windowHeight,
            windowWidth,
            offsetTop,
            offsetLeft,
            event,
            parentRect: boundingElRef.current
              ? boundingElRef.current.getBoundingClientRect()
              : { top: 0, left: 0, right: 0, bottom: 0 }
          })
        )
      } else {
        // If using a bounding parent calculate relative to the parent instead of window
        if (boundingElRef.current) {
          const parentEl = boundingElRef.current
          const parentRect = parentEl.getBoundingClientRect()
          style.position = 'absolute'
          style.top = Math.abs(parentRect.top - targetRect.top) - offsetTop
          style.left =
            Math.abs(targetRect.left - parentRect.left) +
            targetRect.width +
            offsetLeft
        } else {
          // Use window
          style.top = targetRect.top - offsetTop
          style.left = targetRect.left + targetRect.width + offsetLeft
        }
      }

      // Do not animate tooltip while calculating size.
      // Opacity is set to 1 after sizing calculations
      if (
        !hasShownRef.current &&
        tooltipRef.current &&
        typeof getPosition !== 'function'
      ) {
        style.transition = 'none'
        style.opacity = 0
      }

      setTooltipConfig((s) => ({
        ...s,
        ...data,
        targetEl,
        display: true,
        style,
        // x coord on svg document
        svgX: x,
        // y coord on svg document
        svgY: y,
        customStyles
      }))
    },
    [
      customStyles,
      getPosition,
      offsetLeft,
      offsetTop,
      windowHeight,
      windowWidth,
      hideTooltip
    ]
  )

  // Use a small timeout to ensure that the tooltip is not removed from the DOM
  // when the user is moving the mouse directly between 2 graph nodes
  const clearTargetTooltip = useCallback(
    function clearTargetTooltip() {
      if (clearTimeoutDelay) {
        clearTooltipTimeoutRef.current = setTimeout(
          hideTooltip,
          clearTimeoutDelay
        )
      } else {
        hideTooltip()
      }
    },
    [clearTimeoutDelay, hideTooltip]
  )

  return {
    tooltipConfig,
    setTargetTooltip,
    clearTargetTooltip
  }
}

function ChartTooltip({
  darkMode = false,
  animatePosition = false,
  tooltipConfig = {
    style: {},
    title: '',
    content: '',
    display: false,
    /** Used to contain tooltips in svg wrapper */
    boundingElRef: undefined,
    /** Used to contain tooltips in svg wrapper */
    tooltipRef: undefined,
    customStyles: {}
  },
  children = null,
  ...rest
}) {
  const { style, title, content, display, tooltipRef, customStyles } =
    tooltipConfig
  const hasShownOnceRef = useRef(false)

  useEffect(() => {
    if (display) {
      hasShownOnceRef.current = true
    }
  }, [display])

  const bgColor = darkMode ? theme.colors.white[0] : theme.colors.steel[2]
  const fontColor = darkMode ? theme.colors.steel[2] : theme.colors.white[0]
  const testProps = {
    'data-test-hovered-node-title': title,
    'data-test-hovered-node-content': content
  }

  // Utility render fn that isn't a component
  // eslint-disable-next-line react/prop-types
  const render = ({ top, left, forceTransparent, ref } = {}) => (
    <div
      ref={ref}
      {...(!forceTransparent && testProps)}
      css={[
        {
          position: 'fixed',
          maxWidth: theme.space[8],
          padding: theme.space[2],
          background: bgColor,
          borderRadius: theme.borderRadius[0],
          pointerEvents: 'none'
        },
        animatePosition && {
          transition: hasShownOnceRef.current ? tooltipCSSTransition : 'none'
        },
        style,
        // check for both undefined and null. Don't want false when 0
        top != null &&
          left != null && {
            top,
            left
          },
        !display && {
          opacity: 0
        },
        forceTransparent && { opacity: 0 },
        customStyles
      ]}
      {...rest}
    >
      {Boolean(title) && (
        <Text
          as="h6"
          color="white"
          css={{
            lineHeight: '1',
            marginBottom: theme.space[1],
            textAlign: 'left',
            // use the same color for both types for tooltip title
            color: theme.colors.green[0]
          }}
        >
          {title}
        </Text>
      )}
      {Boolean(content) && (
        <Text as="small" css={{ textAlign: 'left', color: fontColor }}>
          {content}
        </Text>
      )}
      {children}
    </div>
  )

  // Here we render a clone of the tooltip to get a full measurement.
  // If the tooltip is already close to the edge the text will break and cause
  // tooltip sizing calculations to be off
  return (
    <Fragment>
      {render({ top: style.top, left: style.left })}
      {render({ top: 0, left: 0, ref: tooltipRef, forceTransparent: true })}
    </Fragment>
  )
}

ChartTooltip.propTypes = {
  tooltipConfig: PropTypes.object,
  darkMode: PropTypes.bool,
  animatePosition: PropTypes.bool,
  children: PropTypes.node
}

export default ChartTooltip
